/**
 * "How did it go" as one row of chips on the ended screen.
 *
 * Chips rather than the menu the call list uses. A menu is right where labels
 * are one of eight things a row menu offers; here it is the only question on
 * the screen and it is asked of every call, so it has to be answerable without
 * opening anything - on a run that is one click instead of three, two hundred
 * times a day.
 *
 * Draws nothing when the registry is empty. That is a real state, not a
 * loading one: `crm.labels.calls` starts out empty and stays that way until
 * somebody fills it in under Settings, and an empty box titled "outcome" would
 * read as something broken.
 */
import type { CSSProperties } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { labelColor } from "../../../lib/crmLabels";
import type { CallOutcomeApi } from "./useCallOutcome";

export function CallOutcome({ outcome }: { outcome: CallOutcomeApi }) {
  const t = useT();
  if (outcome.labels.length === 0) return null;

  return (
    <div className="mt-5 w-full">
      <span className="crm-qsect">{t("calls.queue.outcome")}</span>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {outcome.labels.map((label) => {
          const on = outcome.tags.includes(label.id);
          return (
            <button
              key={label.id}
              type="button"
              aria-pressed={on}
              onClick={() => outcome.toggle(label.id)}
              className={cn("crm-out", on && "is-on")}
              style={{ "--crm-lbl-c": labelColor(label.tone) } as CSSProperties}
            >
              <i />
              {label.name}
            </button>
          );
        })}
      </div>
      {/* Only ever after a write that did not land. A chip that silently went
          back to off would look like a click that missed. */}
      {outcome.failed ? (
        <p className="mt-2 text-xs font-[600] text-[var(--color-destructive)]" role="alert">
          {t("calls.queue.outcomeFailed")}
        </p>
      ) : null}
    </div>
  );
}
